import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseCtl, ServiceLocatorService } from '@ncs/ncs-lib';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface Task {
  id: string;
  title: string;
  description: string;
  status: string;
  dueDate?: Date;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee?: string;
  createdBy?: string;
  labels?: string[];
  members?: string[]; // NEW: list of member display-names / emails
  checklists?: { text: string; checked: boolean }[]; // Persisted checklist items
  createdAt: Date;
  updatedAt: Date;
  order: number;
  appId?: number;
  orgId?: number;
}

export interface Column {
  id: string;
  title: string;
  tasks: Task[];
  color: string;
  icon: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  comment: string;
  authorName: string;
  authorEmail: string;
  createdAt: string;
  isEdited?: boolean;
}

export interface AppUser {
  id: string;
  email: string;
  userFirstname?: string;
  userLastname?: string;
  displayName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-user-task-board',
  templateUrl: './user-task-board.component.html',
  styleUrls: ['./user-task-board.component.css'],
})
export class UserTaskBoardComponent extends BaseCtl implements OnInit {
  // ── State ──────────────────────────────────────────────────────────────────
  isLoading = false;
  errorMessage = '';
  columns: Column[] = [];
  allTasks: Task[] = [];
  selectedTask: Task | null = null;

  // ── Create Task Modal ──────────────────────────────────────────────────────
  showCreateModal = false;
  /** The column id the "Add Task" button was clicked from. */
  createTargetColumnId = 'todo-col';
  isSaving = false;
  saveError = '';
  appUsers: AppUser[] = [];
  isLoadingUsers = false;

  newTask = {
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    assignee: '',
    targetDate: '',
    status: 'Not started',
  };
  // ── Comment Edit feature ───────────────────────────────────────────────────────
  editingCommentId: string | null = null;
  editCommentText = '';
  isSavingCommentEdit = false;
  commentEditError = '';
  // ── Edit Task Modal ────────────────────────────────────────────────────────
  showEditModal = false;
  editingTask: Task | null = null;
  editTargetColumnId = '';
  isEditSaving = false;
  editSaveError = '';

  editForm = {
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    assignee: '',
    targetDate: '',
  };

  // ── Delete confirmation ────────────────────────────────────────────────────
  showDeleteModal = false;
  deletingTaskId = '';
  deletingColumnId = '';
  isDeleting = false;

  // ── Add Column ─────────────────────────────────────────────────────────────
  isAddingColumn = false;
  newColumnTitle = '';
  addColumnError = '';

  // ── Edit Column ────────────────────────────────────────────────────────────
  showEditColumnModal = false;
  editingColumn: Column | null = null;
  editColumnTitle = '';
  editColumnError = '';

  // ── Delete Column ──────────────────────────────────────────────────────────
  showDeleteColumnModal = false;
  deletingColumn: Column | null = null;
  isDeletingColumn = false;

  // ── Members feature ────────────────────────────────────────────────────────
  showMemberPicker = false; // toggle member-picker dropdown in detail modal
  memberSearchQuery = ''; // filter text in picker
  isUpdatingMember = false; // loading state for add/remove calls
  memberError = ''; // inline error in detail modal

  // ── Comments feature ───────────────────────────────────────────────────────
  taskComments: TaskComment[] = [];
  isLoadingComments = false;
  newCommentText = '';
  isSavingComment = false;
  commentError = '';

  // ── Checklist feature ──────────────────────────────────────────────────────
  showChecklistPanel = false;
  checklists: { text: string; checked: boolean }[] = [];
  showAddChecklistInput = false;
  newChecklistItemText = '';
  editingChecklistIndex: number | null = null;
  editingChecklistText = '';

  // ── User context ──────────────────────────────────────────────────────────
  private taskEndpoint = '';
  private keySuffix = '';
  currentUserAppId: number = 0;
  private currentUserOrgId: number = 0;
  currentUserEmail = '';
  currentUserName = '';

  // ── Column definitions ─────────────────────────────────────────────────────
  private readonly COLUMN_DEFS: Omit<Column, 'tasks'>[] = [
    {
      id: 'todo-col',
      title: 'To Do',
      color: '#4A90E2',
      icon: 'radio_button_unchecked',
    },
    {
      id: 'inprogress-col',
      title: 'In Progress',
      color: '#4A90E2',
      icon: 'timelapse',
    },
    {
      id: 'review-col',
      title: 'Review',
      color: '#4A90E2',
      icon: 'rate_review',
    },
    { id: 'done-col', title: 'Done', color: '#4A90E2', icon: 'check_circle' },
  ];

  // ── Status → column mapping ───────────────────────────────────────────────
  private readonly STATUS_TO_COL: { [key: string]: string } = {
    'Not started': 'todo-col',
    todo: 'todo-col',
    'In Progress': 'inprogress-col',
    inprogress: 'inprogress-col',
    Review: 'review-col',
    review: 'review-col',
    Completed: 'done-col',
    done: 'done-col',
  };

  // ── Column → status mapping (reverse, used when a card is dropped) ────────
  private readonly COL_TO_STATUS: { [key: string]: string } = {
    'todo-col': 'Not started',
    'inprogress-col': 'In Progress',
    'review-col': 'Review',
    'done-col': 'Completed',
  };

  // Dynamic mapping kept in sync when custom columns are added
  private dynamicColToStatus: { [key: string]: string } = {};

  // ── Backend column persistence ────────────────────────────────────────────

  /**
   * Fetch all persisted custom columns for the current app from the backend
   * and merge them into the board after the default columns.
   * Called once from ngOnInit after resolveUserContext() completes.
   * Returns a Promise so the caller can await it before loadTasks().
   */
  private loadColumnsFromBackend(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.taskEndpoint || !this.currentUserAppId) {
        resolve();
        return;
      }

      const url = `${this.taskEndpoint}/columns/${this.currentUserAppId}`;
      this.locator.http.get(url, (res: any, error: boolean) => {
        if (!error && res?.success) {
          const raw: any[] = res.result?.data || res.result || [];
          raw
            .filter((c: any) => c.recordCount == null)
            .forEach((c: any) => {
              const colId = String(c.id || c._id || '');
              // Skip if it already exists (e.g. default columns matched by id)
              if (!colId || this.columns.find((col) => col.id === colId))
                return;
              const col: Column = {
                id: colId,
                title: c.title || 'Untitled',
                color: c.color || '#4A90E2',
                icon: c.icon || 'view_column',
                tasks: [],
              };
              this.columns.push(col);
              this.dynamicColToStatus[colId] = col.title;
            });
        }
        resolve();
      });
    });
  }

  get columnIds(): string[] {
    return this.columns.map((c) => c.id);
  }

  readonly priorityOptions: Task['priority'][] = [
    'low',
    'medium',
    'high',
    'critical',
  ];

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
  ) {
    super('', locator, route);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.buildEmptyColumns();
    this.resolveUserContext().then(() => {
      if (!this.currentUserAppId) {
        this.errorMessage =
          'App context could not be resolved. Please re-login.';
        return;
      }
      // Load persisted custom columns from the backend first,
      // then load tasks so they distribute into the correct columns.
      this.loadColumnsFromBackend().then(() => {
        this.loadTasks();
        this.loadAppUsers();
      });
    });
  }

  // ─── User context resolution ───────────────────────────────────────────────

  private async resolveUserContext(): Promise<void> {
    const contextPath = await this.locator.config.getProperty('CONTEXT_PATH');
    this.keySuffix = contextPath + '-';
    this.taskEndpoint = await this.locator.config.getProperty('endpoint.task');

    // Retrieve appId (scoped per application)
    const appIdRaw = localStorage.getItem(this.keySuffix + 'appId');
    this.currentUserAppId = appIdRaw ? Number(appIdRaw) : 0;

    // Retrieve orgId (scoped per organization) — same pattern as appId
    const orgIdRaw = localStorage.getItem(this.keySuffix + 'orgId');
    this.currentUserOrgId = orgIdRaw ? Number(orgIdRaw) : 0;

    // Retrieve current user identity for assignee filtering
    const ucRaw = localStorage.getItem(this.keySuffix + 'userContext');
    if (ucRaw) {
      try {
        const uc = JSON.parse(ucRaw);
        this.currentUserEmail = (uc.email || uc.userEmail || '').trim();
        this.currentUserName = (
          uc.name ||
          uc.firstName ||
          uc.userFirstname ||
          ''
        ).trim();
      } catch {
        // ignore
      }
    }
  }

  // ─── Load app users for assignee dropdown ─────────────────────────────────

  loadAppUsers(): void {
    if (!this.currentUserAppId) return;

    this.isLoadingUsers = true;
    const url = `${this.taskEndpoint}/app-users/${this.currentUserAppId}`;

    this.locator.http.get(url, (res: any, error: boolean) => {
      this.isLoadingUsers = false;
      if (error || !res?.success) return;

      const raw: any[] = res.result?.data || [];
      this.appUsers = raw.map((u) => {
        const first = u.userFirstname || u.firstName || '';
        const last = u.userLastname || u.lastName || '';
        const email = u.email || u.userEmail || '';
        const fullName = [first, last].filter(Boolean).join(' ').trim();
        return {
          id: String(u.id || u._id || ''),
          email,
          userFirstname: first,
          userLastname: last,
          displayName: fullName || email,
        };
      });
      this.cdRef.detectChanges();
    });
  }

  // ─── Data loading ──────────────────────────────────────────────────────────

  loadTasks(): void {
    if (!this.taskEndpoint || !this.currentUserAppId) {
      this.errorMessage =
        'Unable to determine your application. Please re-login.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const url = `${this.taskEndpoint}/search/0`;
    const payload: any = {
      pageSize: 500,
      appId: this.currentUserAppId,
      orderBy: 'order',
      asc: 'ASC',
      isGroupFilter: false, // show ALL app tasks, not just those I created
    };

    if (this.currentUserOrgId) {
      payload['orgId'] = this.currentUserOrgId;
    }

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isLoading = false;

      if (error || !res?.success) {
        this.errorMessage = 'Failed to load tasks. Please try again later.';
        this.cdRef.detectChanges();
        return;
      }

      const rawTasks: any[] = res.result?.data || [];
      this.allTasks = rawTasks
        .filter((t) => t.recordCount == null) // strip meta row
        .map((t) => this.mapRaw(t));
      this.distributeTasksToColumns();
      this.cdRef.detectChanges();
    });
  }

  private mapRaw(raw: any): Task {
    // Parse members — stored as comma-separated string in DB
    let members: string[] = [];
    if (raw.members) {
      members = Array.isArray(raw.members)
        ? raw.members
        : String(raw.members)
            .split(',')
            .map((m: string) => m.trim())
            .filter(Boolean);
    }

    return {
      id: String(raw.id || raw._id || `task-${Date.now()}`),
      title: raw.title || 'Untitled',
      description: raw.description || '',
      status: raw.status || 'Not started',
      priority: this.normalisePriority(raw.priority),
      assignee: raw.assignee || raw.userName || '',
      createdBy: raw.createdBy || raw.userName || '',
      labels: raw.labels
        ? Array.isArray(raw.labels)
          ? raw.labels
          : raw.labels.split(',').map((l: string) => l.trim())
        : [],
      members,
      checklists: Array.isArray(raw.checklists) ? raw.checklists : [],
      dueDate: raw.targetDate ? new Date(raw.targetDate) : undefined,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
      order: raw.order ?? 0,
      appId: raw.appId,
      orgId: raw.orgId,
    };
  }

  private normalisePriority(p: any): Task['priority'] {
    const valid = ['low', 'medium', 'high', 'critical'];
    const lc = (p || '').toLowerCase();
    return valid.includes(lc) ? (lc as Task['priority']) : 'medium';
  }

  // ─── Board building ────────────────────────────────────────────────────────

  private buildEmptyColumns(): void {
    this.columns = this.COLUMN_DEFS.map((def) => ({ ...def, tasks: [] }));
    // Custom columns are now fetched from the backend in loadColumnsFromBackend(),
    // called after resolveUserContext() completes.
  }

  private distributeTasksToColumns(): void {
    // Rebuild the reverse map (status → colId) for all custom columns
    // so tasks saved with a custom status route correctly after reload
    this.columns.forEach((col) => {
      if (!this.COLUMN_DEFS.some((d) => d.id === col.id)) {
        this.dynamicColToStatus[col.id] = col.title;
      }
    });

    // Build a status→colId lookup that covers both built-in and custom columns
    const statusToColId: { [status: string]: string } = {
      ...this.STATUS_TO_COL,
    };
    Object.entries(this.dynamicColToStatus).forEach(([colId, status]) => {
      statusToColId[status] = colId;
    });

    // Reset task arrays
    this.columns.forEach((col) => (col.tasks = []));

    this.allTasks.forEach((task) => {
      const colId = statusToColId[task.status] || 'todo-col';
      const col = this.columns.find((c) => c.id === colId);
      if (col) col.tasks.push(task);
    });
    this.columns.forEach((col) => col.tasks.sort((a, b) => a.order - b.order));
  }

  // ─── Add Column ────────────────────────────────────────────────────────────

  toggleAddColumn(): void {
    this.isAddingColumn = !this.isAddingColumn;
    this.newColumnTitle = '';
    this.addColumnError = '';
    if (this.isAddingColumn) {
      setTimeout(() => {
        const input = document.querySelector(
          '.new-column-input',
        ) as HTMLInputElement;
        if (input) input.focus();
      }, 50);
    }
  }

  addColumn(): void {
    const title = this.newColumnTitle.trim();
    if (!title) {
      this.addColumnError = 'Column name cannot be empty.';
      return;
    }

    const color = this.getRandomColor();
    const icon = 'view_column';
    const colStatus = title; // custom status equals the column title

    // Optimistically add to UI immediately
    const tempId = `custom-col-${Date.now()}`;
    const newColumn: Column = {
      id: tempId,
      title,
      tasks: [],
      color,
      icon,
    };
    this.dynamicColToStatus[tempId] = colStatus;
    this.columns = [...this.columns, newColumn];
    this.newColumnTitle = '';
    this.addColumnError = '';
    this.isAddingColumn = false;
    this.cdRef.detectChanges();

    // Persist to backend
    const url = `${this.taskEndpoint}/column/save`;
    const payload = {
      title,
      color,
      icon,
      appId: this.currentUserAppId,
    };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      if (error || !res?.success) {
        // Rollback optimistic add
        this.columns = this.columns.filter((c) => c.id !== tempId);
        delete this.dynamicColToStatus[tempId];
        this.addColumnError =
          res?.message || 'Failed to save column. Please try again.';
        this.cdRef.detectChanges();
        return;
      }

      // Replace tempId with the real database id
      const savedId = String(
        res?.result?.data?.id || res?.result?.id || tempId,
      );
      if (savedId !== tempId) {
        const col = this.columns.find((c) => c.id === tempId);
        if (col) {
          col.id = savedId;
          this.dynamicColToStatus[savedId] = colStatus;
          delete this.dynamicColToStatus[tempId];
          // CDK drag-drop uses column ids; trigger re-render
          this.columns = [...this.columns];
        }
      }
      this.cdRef.detectChanges();
    });
  }

  // ─── Edit Column ───────────────────────────────────────────────────────────

  openEditColumnModal(column: Column, event: Event): void {
    event.stopPropagation();
    this.editingColumn = column;
    this.editColumnTitle = column.title;
    this.editColumnError = '';
    this.showEditColumnModal = true;
  }

  closeEditColumnModal(): void {
    this.showEditColumnModal = false;
    this.editingColumn = null;
    this.editColumnTitle = '';
    this.editColumnError = '';
  }

  saveEditedColumn(): void {
    const title = this.editColumnTitle.trim();
    if (!title) {
      this.editColumnError = 'Column name cannot be empty.';
      return;
    }
    if (!this.editingColumn) return;

    const column = this.editingColumn;
    const oldTitle = column.title;

    // Optimistic update
    column.title = title;
    if (this.dynamicColToStatus[column.id] !== undefined) {
      this.dynamicColToStatus[column.id] = title;
    }
    this.closeEditColumnModal();
    this.cdRef.detectChanges();

    // Only persist if it has a numeric id (i.e. was saved to backend)
    const numId = this.parseNumericId(column.id);
    if (!numId) return; // default columns or temp — no backend call needed

    const url = `${this.taskEndpoint}/column/update`;
    const payload = { id: numId, title };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      if (error || !res?.success) {
        // Rollback
        column.title = oldTitle;
        if (this.dynamicColToStatus[column.id] !== undefined) {
          this.dynamicColToStatus[column.id] = oldTitle;
        }
        this.cdRef.detectChanges();
      }
    });
  }

  // ─── Delete Column ─────────────────────────────────────────────────────────

  openDeleteColumnModal(column: Column, event: Event): void {
    event.stopPropagation();
    this.deletingColumn = column;
    this.showDeleteColumnModal = true;
  }

  closeDeleteColumnModal(): void {
    this.showDeleteColumnModal = false;
    this.deletingColumn = null;
    this.isDeletingColumn = false;
  }

  confirmDeleteColumn(): void {
    if (!this.deletingColumn) return;
    this.isDeletingColumn = true;

    const colId = this.deletingColumn.id;
    const tasksToDelete = [...(this.deletingColumn.tasks || [])];

    // Remove all tasks in the column from backend
    tasksToDelete.forEach((task) => {
      const numId = this.parseNumericId(task.id);
      if (numId) {
        const url = `${this.taskEndpoint}/delete/${numId}`;
        this.locator.http.get(url, () => {});
      }
    });

    // Remove tasks from allTasks
    const taskIds = new Set(tasksToDelete.map((t) => t.id));
    this.allTasks = this.allTasks.filter((t) => !taskIds.has(t.id));

    // Remove column from UI
    this.columns = this.columns.filter((c) => c.id !== colId);
    delete this.dynamicColToStatus[colId];
    this.closeDeleteColumnModal();
    this.cdRef.detectChanges();

    // Persist column deletion to backend (only for backend-stored columns with numeric ids)
    const numColId = this.parseNumericId(colId);
    if (numColId) {
      const url = `${this.taskEndpoint}/column/delete/${numColId}`;
      this.locator.http.post(url, {}, (res: any, error: boolean) => {
        if (error || !res?.success) {
          console.error('Failed to delete column from backend:', res?.message);
        }
      });
    }
  }

  private getRandomColor(): string {
    return '#4A90E2';
  }

  // ─── Drag & drop ────────────────────────────────────────────────────────────

  drop(event: CdkDragDrop<Task[]>, targetColId: string): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      return;
    }

    const task = event.item.data as Task;
    const previousColId = event.previousContainer.id;

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );

    const newStatus =
      this.COL_TO_STATUS[targetColId] ||
      this.dynamicColToStatus[targetColId] ||
      targetColId;
    if (!newStatus) return;

    const oldStatus = task.status;
    task.status = newStatus;

    this.updateTaskStatus(task, oldStatus, previousColId);
  }

  private updateTaskStatus(
    task: Task,
    oldStatus: string,
    previousColId: string,
  ): void {
    const url = `${this.taskEndpoint}/status/${task.id}/${encodeURIComponent(task.status)}`;

    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error || !res?.success) {
        task.status = oldStatus;
        this.distributeTasksToColumns();
        this.errorMessage = 'Could not update task status. Please try again.';
        this.cdRef.detectChanges();
      }
    });
  }

  // ─── Add Task (per-column) ─────────────────────────────────────────────────

  openCreateModal(columnId: string = 'todo-col'): void {
    this.createTargetColumnId = columnId;
    const status =
      this.COL_TO_STATUS[columnId] ||
      this.dynamicColToStatus[columnId] ||
      columnId;
    this.newTask = {
      title: '',
      description: '',
      priority: 'medium',
      assignee: '',
      targetDate: '',
      status,
    };
    this.saveError = '';
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.saveError = '';
  }

  /** Returns the column title for the current create-target column (used in modal header). */
  getCreateColumnTitle(): string {
    const col = this.columns.find((c) => c.id === this.createTargetColumnId);
    return col?.title || 'To Do';
  }

  /** Returns the accent color for the current create-target column. */
  getCreateColumnColor(): string {
    const col = this.columns.find((c) => c.id === this.createTargetColumnId);
    return col?.color || '#0052CC';
  }

  saveNewTask(): void {
    if (!this.newTask.title.trim()) {
      this.saveError = 'Task title is required.';
      return;
    }

    this.isSaving = true;
    this.saveError = '';

    const currentUser = this.currentUserName || this.currentUserEmail;
    const targetStatus =
      this.COL_TO_STATUS[this.createTargetColumnId] ||
      this.dynamicColToStatus[this.createTargetColumnId] ||
      this.createTargetColumnId;

    // Calculate order: tasks in the target column length
    const targetCol = this.columns.find(
      (c) => c.id === this.createTargetColumnId,
    );
    const orderInColumn = targetCol
      ? targetCol.tasks.length
      : this.allTasks.length;

    const payload: any = {
      title: this.newTask.title.trim(),
      description: this.newTask.description.trim(),
      priority: this.newTask.priority,
      assignee: currentUser,
      status: targetStatus,
      appId: this.currentUserAppId,
      createdBy: currentUser,
      order: orderInColumn,
    };

    if (this.currentUserOrgId) {
      payload['orgId'] = this.currentUserOrgId;
    }

    if (this.newTask.targetDate) {
      payload['targetDate'] = this.newTask.targetDate;
    }

    const url = `${this.taskEndpoint}/save`;

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isSaving = false;
      if (error || !res?.success) {
        this.saveError =
          res?.message || 'Failed to create task. Please try again.';
        this.cdRef.detectChanges();
        return;
      }

      this.showCreateModal = false;

      const savedId = String(
        res?.result?.data?.id || res?.result?.id || `temp-${Date.now()}`,
      );

      const newTaskObj: Task = {
        id: savedId,
        title: payload.title,
        description: payload.description,
        priority: this.newTask.priority,
        assignee: currentUser,
        createdBy: currentUser,
        status: targetStatus,
        labels: [],
        members: [],
        dueDate: this.newTask.targetDate
          ? new Date(this.newTask.targetDate)
          : undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
        order: payload.order,
        appId: this.currentUserAppId,
        orgId: this.currentUserOrgId || undefined,
      };

      this.allTasks.push(newTaskObj);
      if (targetCol) {
        targetCol.tasks.push(newTaskObj);
      }
      this.cdRef.detectChanges();

      // Reload to get fresh data with a real id
      this.loadTasks();
    });
  }

  // ─── Edit Task ─────────────────────────────────────────────────────────────

  /**
   * Opens the Edit Task modal for an existing task.
   * Stops the click event from bubbling to the card's openTaskDetail handler.
   */
  openEditModal(task: Task, columnId: string, event: Event): void {
    event.stopPropagation();

    this.editingTask = { ...task };
    this.editTargetColumnId = columnId;

    this.editForm = {
      title: task.title,
      description: task.description,
      priority: task.priority,
      assignee: task.assignee || '',
      targetDate: task.dueDate ? this.formatDateForInput(task.dueDate) : '',
    };

    this.editSaveError = '';
    this.isEditSaving = false;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingTask = null;
    this.editSaveError = '';
  }

  saveEditedTask(): void {
    if (!this.editForm.title.trim()) {
      this.editSaveError = 'Task title is required.';
      return;
    }

    if (!this.editingTask) return;

    this.isEditSaving = true;
    this.editSaveError = '';

    const numId = this.parseNumericId(this.editingTask.id);
    if (!numId) {
      this.editSaveError = 'Invalid task ID.';
      this.isEditSaving = false;
      return;
    }

    const payload: any = {
      id: numId,
      title: this.editForm.title.trim(),
      description: this.editForm.description.trim(),
      priority: this.editForm.priority,
      assignee: this.editForm.assignee || this.editingTask.assignee || '',
      status: this.editingTask.status,
      appId: this.currentUserAppId,
      order: this.editingTask.order,
    };

    if (this.currentUserOrgId) {
      payload['orgId'] = this.currentUserOrgId;
    }

    if (this.editForm.targetDate) {
      payload['targetDate'] = this.editForm.targetDate;
    }

    const url = `${this.taskEndpoint}/save`;

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isEditSaving = false;
      if (error || !res?.success) {
        this.editSaveError =
          res?.message || 'Failed to update task. Please try again.';
        this.cdRef.detectChanges();
        return;
      }

      // Update in-memory data immediately for snappy UX
      const updatedTask: Task = {
        ...this.editingTask!,
        title: this.editForm.title.trim(),
        description: this.editForm.description.trim(),
        priority: this.editForm.priority,
        assignee: this.editForm.assignee || this.editingTask!.assignee || '',
        dueDate: this.editForm.targetDate
          ? new Date(this.editForm.targetDate)
          : undefined,
        updatedAt: new Date(),
      };

      // Sync allTasks
      const allIdx = this.allTasks.findIndex(
        (t) => t.id === this.editingTask!.id,
      );
      if (allIdx !== -1) this.allTasks[allIdx] = updatedTask;

      // Sync the column's task list
      const col = this.columns.find((c) => c.id === this.editTargetColumnId);
      if (col) {
        const colIdx = col.tasks.findIndex(
          (t) => t.id === this.editingTask!.id,
        );
        if (colIdx !== -1) col.tasks[colIdx] = updatedTask;
      }

      this.showEditModal = false;
      this.editingTask = null;
      this.cdRef.detectChanges();
    });
  }

  // ─── Delete Task ────────────────────────────────────────────────────────────

  /**
   * Opens the inline delete-confirmation modal.
   * Stops the click event from bubbling to the card's openTaskDetail handler.
   */
  openDeleteModal(taskId: string, columnId: string, event: Event): void {
    event.stopPropagation();
    this.deletingTaskId = taskId;
    this.deletingColumnId = columnId;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deletingTaskId = '';
    this.deletingColumnId = '';
    this.isDeleting = false;
  }

  confirmDelete(): void {
    const numId = this.parseNumericId(this.deletingTaskId);
    if (!numId) {
      this.closeDeleteModal();
      return;
    }

    this.isDeleting = true;

    // Remove from UI immediately for snappy UX
    const col = this.columns.find((c) => c.id === this.deletingColumnId);
    if (col) {
      col.tasks = col.tasks.filter((t) => t.id !== this.deletingTaskId);
    }
    this.allTasks = this.allTasks.filter((t) => t.id !== this.deletingTaskId);

    const url = `${this.taskEndpoint}/delete/${numId}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error) {
        console.error('Task delete failed', error);
      }
      this.closeDeleteModal();
      this.cdRef.detectChanges();
    });
  }

  // ─── Members feature ───────────────────────────────────────────────────────

  /** Users from the same app who are NOT yet members of the selected task */
  get availableUsersToAdd(): AppUser[] {
    if (!this.selectedTask) return [];
    const currentMembers = this.selectedTask.members || [];
    return this.appUsers.filter(
      (u) =>
        !currentMembers.includes(u.displayName) &&
        u.displayName !== this.selectedTask!.assignee &&
        (this.memberSearchQuery === '' ||
          u.displayName
            .toLowerCase()
            .includes(this.memberSearchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(this.memberSearchQuery.toLowerCase())),
    );
  }

  toggleMemberPicker(): void {
    this.showMemberPicker = !this.showMemberPicker;
    this.memberSearchQuery = '';
  }

  closeMemberPicker(): void {
    this.showMemberPicker = false;
    this.memberSearchQuery = '';
  }

  addMember(user: AppUser): void {
    if (!this.selectedTask) return;

    const members = this.selectedTask.members || [];
    if (members.includes(user.displayName)) return;

    this.isUpdatingMember = true;
    this.memberError = '';

    const url = `${this.taskEndpoint}/members/${this.selectedTask.id}`;
    const payload = { userId: user.displayName, action: 'add' };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isUpdatingMember = false;
      if (error || !res?.success) {
        this.memberError = 'Failed to add member. Please try again.';
        this.cdRef.detectChanges();
        return;
      }
      // Optimistic update
      if (!this.selectedTask!.members) this.selectedTask!.members = [];
      this.selectedTask!.members = [
        ...this.selectedTask!.members,
        user.displayName,
      ];
      // Sync back to allTasks
      this.syncSelectedTaskToBoard();
      this.showMemberPicker = false;
      this.memberSearchQuery = '';
      this.cdRef.detectChanges();
    });
  }

  removeMember(memberName: string): void {
    if (!this.selectedTask) return;

    // Prevent removing if it's the only member or if you want to keep at least one
    this.isUpdatingMember = true;
    this.memberError = '';

    const url = `${this.taskEndpoint}/members/${this.selectedTask.id}`;
    const payload = { userId: memberName, action: 'remove' };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isUpdatingMember = false;
      if (error || !res?.success) {
        this.memberError = 'Failed to remove member. Please try again.';
        this.cdRef.detectChanges();
        return;
      }
      // Optimistic update - remove from local array
      this.selectedTask!.members = (this.selectedTask!.members || []).filter(
        (m) => m !== memberName,
      );
      this.syncSelectedTaskToBoard();
      this.cdRef.detectChanges();
    });
  }
  /** Keep allTasks and columns in sync after member changes */
  private syncSelectedTaskToBoard(): void {
    if (!this.selectedTask) return;
    const idx = this.allTasks.findIndex((t) => t.id === this.selectedTask!.id);
    if (idx !== -1) {
      this.allTasks[idx] = {
        ...this.allTasks[idx],
        members: [...(this.selectedTask!.members || [])],
      };
    }
    // Also update the column's task array reference
    this.columns.forEach((col) => {
      const ti = col.tasks.findIndex((t) => t.id === this.selectedTask!.id);
      if (ti !== -1) {
        col.tasks[ti] = {
          ...col.tasks[ti],
          members: [...(this.selectedTask!.members || [])],
        };
      }
    });
  }

  // ─── Task detail modal ─────────────────────────────────────────────────────

  openTaskDetail(task: Task): void {
    this.selectedTask = { ...task, members: [...(task.members || [])] };
    this.showMemberPicker = false;
    this.memberSearchQuery = '';
    this.memberError = '';
    // reset & load comments
    this.taskComments = [];
    this.newCommentText = '';
    this.commentError = '';
    this.loadComments(task.id);
    // Load checklist from task data (persisted in DB)
    this.showChecklistPanel = false;
    this.checklists = task.checklists ? [...task.checklists] : [];
    this.showAddChecklistInput = false;
    this.newChecklistItemText = '';
    this.editingChecklistIndex = null;
    this.editingChecklistText = '';
  }

  closeTaskDetail(): void {
    this.selectedTask = null;
    this.showMemberPicker = false;
    this.memberSearchQuery = '';
    this.memberError = '';
    this.taskComments = [];
    this.newCommentText = '';
    this.commentError = '';
    // Reset checklist state
    this.showChecklistPanel = false;
    this.checklists = [];
    this.showAddChecklistInput = false;
    this.newChecklistItemText = '';
    this.editingChecklistIndex = null;
    this.editingChecklistText = '';
  }

  // ─── Comments ───────────────────────────────────────────────────────────────

  // loadComments(taskId: string): void {
  //   this.isLoadingComments = true;
  //   const url = `${this.taskEndpoint}/comment/list/${taskId}`;
  //   this.locator.http.get(url, (res: any, error: boolean) => {
  //     this.isLoadingComments = false;
  //     if (error || !res?.success) {
  //       this.cdRef.detectChanges();
  //       return;
  //     }
  //     const raw: any[] = res.result?.data || [];
  //     this.taskComments = raw.map((c) => ({
  //       id: String(c.id || c._id || ''),
  //       taskId: String(c.taskId || ''),
  //       comment: c.comment || '',
  //       authorName: c.authorName || c.createdBy || 'Unknown',
  //       authorEmail: c.authorEmail || '',
  //       createdAt: c.createdAt || '',
  //     }));
  //     this.cdRef.detectChanges();
  //   });
  // }

  addComment(): void {
    if (!this.newCommentText.trim() || !this.selectedTask) return;
    this.isSavingComment = true;
    this.commentError = '';

    const url = `${this.taskEndpoint}/comment/save`;
    const payload = {
      taskId: this.selectedTask.id,
      comment: this.newCommentText.trim(),
    };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isSavingComment = false;
      if (error || !res?.success) {
        this.commentError = 'Failed to add comment. Please try again.';
        this.cdRef.detectChanges();
        return;
      }
      this.newCommentText = '';
      this.loadComments(this.selectedTask!.id);
    });
  }

  // deleteComment(commentId: string): void {
  //   if (!commentId) return;
  //   const url = `${this.taskEndpoint}/comment/delete/${commentId}`;
  //   this.locator.http.delete(url, (res: any, error: boolean) => {
  //     if (error || !res?.success) return;
  //     this.taskComments = this.taskComments.filter((c) => c.id !== commentId);
  //     this.cdRef.detectChanges();
  //   });
  // }

  formatCommentDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ─── Display helpers ───────────────────────────────────────────────────────

  totalTasks(): number {
    return this.allTasks.length;
  }

  completedTasks(): number {
    return this.allTasks.filter((t) => t.status === 'Completed').length;
  }

  inProgressTasks(): number {
    return this.allTasks.filter((t) => t.status === 'In Progress').length;
  }

  getPriorityColor(priority: Task['priority']): string {
    const map: Record<string, string> = {
      low: '#7ED321',
      medium: '#F5A623',
      high: '#FF6B6B',
      critical: '#9013FE',
    };
    return map[priority] || '#aaa';
  }

  getPriorityLabel(priority: string): string {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    const diffDays = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
    return `Due in ${diffDays}d`;
  }

  isOverdue(date: Date | undefined): boolean {
    if (!date) return false;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return Date.now() > d.getTime();
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      'Not started': 'status-not-started',
      'In Progress': 'status-in-progress',
      Review: 'status-review',
      Completed: 'status-completed',
    };
    return map[status] || 'status-not-started';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  /** Avatar background colours — cycles through a palette per name */
  getMemberColor(name: string): string {
    const palette = [
      '#4A90E2',
      '#E67E22',
      '#27AE60',
      '#8E44AD',
      '#C0392B',
      '#16A085',
      '#D35400',
      '#2980B9',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++)
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private parseNumericId(id: string): number | null {
    const n = Number(id);
    return isNaN(n) || n <= 0 ? null : n;
  }

  private formatDateForInput(date: Date): string {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  startEditComment(comment: TaskComment): void {
    this.editingCommentId = comment.id;
    this.editCommentText = comment.comment;
    this.commentEditError = '';
  }

  /**
   * Cancel comment editing
   */
  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editCommentText = '';
    this.commentEditError = '';
  }

  /**
   * Save edited comment
   */
  saveEditedComment(): void {
    if (!this.editingCommentId || !this.editCommentText.trim()) {
      this.commentEditError = 'Comment text cannot be empty.';
      return;
    }

    this.isSavingCommentEdit = true;
    this.commentEditError = '';

    const url = `${this.taskEndpoint}/comment/update`;
    const payload = {
      id: this.editingCommentId,
      comment: this.editCommentText.trim(),
    };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      this.isSavingCommentEdit = false;

      if (error || !res?.success) {
        this.commentEditError =
          res?.message || 'Failed to update comment. Please try again.';
        this.cdRef.detectChanges();
        return;
      }

      // Update the comment in the local array
      const commentIndex = this.taskComments.findIndex(
        (c) => c.id === this.editingCommentId,
      );
      if (commentIndex !== -1) {
        this.taskComments[commentIndex] = {
          ...this.taskComments[commentIndex],
          comment: this.editCommentText.trim(),
          isEdited: true,
        };
      }

      // Clear edit state
      this.editingCommentId = null;
      this.editCommentText = '';
      this.commentEditError = '';
      this.cdRef.detectChanges();
    });
  }

  /**
   * Delete a comment with confirmation
   */
  // deleteComment(commentId: string): void {
  //   if (!commentId) return;

  //   const comment = this.taskComments.find((c) => c.id === commentId);
  //   if (!comment) return;

  //   if (!confirm(`Delete comment from ${comment.authorName}?`)) {
  //     return;
  //   }

  //   const url = `${this.taskEndpoint}/comment/delete/${commentId}`;

  //   this.locator.http.post(url, (res: any, error: boolean) => {
  //     if (error) {
  //       console.error('Failed to delete comment:', error);
  //       this.commentError = 'Failed to delete comment. Please try again.';
  //       this.cdRef.detectChanges();
  //       return;
  //     }

  //     // Check if response is successful
  //     // Response format: { success: true, message: "...", result: {...} }
  //     if (res && (res.success === false || res.result === false)) {
  //       this.commentError = res.message || 'Failed to delete comment.';
  //       this.cdRef.detectChanges();
  //       return;
  //     }

  //     // Remove the comment from local array
  //     this.taskComments = this.taskComments.filter((c) => c.id !== commentId);
  //     this.cdRef.detectChanges();
  //   });
  // }
  deleteComment(commentId: string): void {
    if (!commentId) return;

    const comment = this.taskComments.find((c) => c.id === commentId);
    if (!comment) return;

    if (!confirm(`Delete comment from ${comment.authorName}?`)) {
      return;
    }

    // ✅ Store for rollback
    const previousComments = [...this.taskComments];
    const deletedComment = comment;

    // ✅ Instant UI removal
    this.taskComments = this.taskComments.filter((c) => c.id !== commentId);
    this.cdRef.detectChanges();

    const url = `${this.taskEndpoint}/comment/delete/${commentId}`;

    console.log('🔄 Deleting comment:', commentId);

    // ✅ Use POST (since backend has @PostMapping)
    this.locator.http.post(url, {}, (res: any, error: boolean) => {
      console.log('📦 Delete response:', res);
      console.log('❌ Error:', error);

      if (error) {
        console.error('❌ Delete failed with error');
        // Restore comment
        this.taskComments = previousComments;
        this.commentError = 'Failed to delete comment. Please try again.';
        this.cdRef.detectChanges();
        return;
      }

      // ✅ Check response
      if (res && res.success === true) {
        console.log('✅ Comment permanently deleted from database');
        // Ensure removed from UI
        this.taskComments = this.taskComments.filter((c) => c.id !== commentId);
        this.cdRef.detectChanges();
      } else {
        console.error('❌ Server returned failure:', res?.message);
        // Restore comment
        this.taskComments = previousComments;
        this.commentError = res?.message || 'Failed to delete comment.';
        this.cdRef.detectChanges();
      }
    });
  }

  // ─── Update loadComments to handle response properly ────────────────────────

  // Update the loadComments method to handle the response format correctly:
  loadComments(taskId: string): void {
    this.isLoadingComments = true;
    const url = `${this.taskEndpoint}/comment/list/${taskId}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      this.isLoadingComments = false;
      if (error || !res?.success) {
        this.cdRef.detectChanges();
        return;
      }
      // Handle both response formats: result.data or result
      const raw = res.result?.data || res.result || [];
      this.taskComments = raw.map((c: any) => ({
        id: String(c.id || c._id || ''),
        taskId: String(c.taskId || ''),
        comment: c.comment || '',
        authorName: c.authorName || c.createdBy || 'Unknown',
        authorEmail: c.authorEmail || '',
        createdAt: c.createdAt || '',
        isEdited: c.isEdited || false,
      }));
      this.cdRef.detectChanges();
    });
  }

  // ─── Checklist methods ─────────────────────────────────────────────────────

  /** Persist current checklists array into the task record in MongoDB */
  private saveChecklistToDB(): void {
    if (!this.selectedTask) return;

    const numId = this.parseNumericId(this.selectedTask.id);
    if (!numId) return;

    // Use dedicated checklist endpoint that bypasses InputValidator
    const url = `${this.taskEndpoint}/checklist/save`;
    const payload = {
      id: numId,
      checklists: this.checklists,
    };

    this.locator.http.post(url, payload, (res: any, error: boolean) => {
      if (!error && res?.success) {
        // Sync in-memory task so re-opening the detail reflects changes
        if (this.selectedTask) {
          this.selectedTask.checklists = [...this.checklists];
        }
        const t = this.allTasks.find((t) => t.id === this.selectedTask?.id);
        if (t) t.checklists = [...this.checklists];
        this.cdRef.detectChanges();
      }
    });
  }

  toggleChecklistPanel(): void {
    this.showChecklistPanel = !this.showChecklistPanel;
    if (this.showChecklistPanel && this.checklists.length === 0) {
      // Auto-open add input when first opening
      this.showAddChecklistInput = true;
    }
  }

  openAddChecklistInput(): void {
    this.showAddChecklistInput = true;
    this.newChecklistItemText = '';
  }

  cancelAddChecklist(): void {
    this.showAddChecklistInput = false;
    this.newChecklistItemText = '';
  }

  addChecklistItem(): void {
    const text = this.newChecklistItemText.trim();
    if (!text) return;
    this.checklists.push({ text, checked: false });
    this.newChecklistItemText = '';
    this.showAddChecklistInput = false;
    this.saveChecklistToDB();
  }

  toggleChecklistItem(index: number): void {
    if (index >= 0 && index < this.checklists.length) {
      this.checklists[index].checked = !this.checklists[index].checked;
      this.saveChecklistToDB();
    }
  }

  startEditChecklist(index: number): void {
    this.editingChecklistIndex = index;
    this.editingChecklistText = this.checklists[index].text;
  }

  saveChecklistEdit(index: number): void {
    const text = this.editingChecklistText.trim();
    if (text && index >= 0 && index < this.checklists.length) {
      this.checklists[index].text = text;
    }
    this.editingChecklistIndex = null;
    this.editingChecklistText = '';
    this.saveChecklistToDB();
  }

  cancelChecklistEdit(): void {
    this.editingChecklistIndex = null;
    this.editingChecklistText = '';
  }

  deleteChecklistItem(index: number): void {
    this.checklists.splice(index, 1);
    if (this.editingChecklistIndex === index) {
      this.editingChecklistIndex = null;
    }
    this.saveChecklistToDB();
  }

  getCheckedCount(): number {
    return this.checklists.filter((i) => i.checked).length;
  }

  getChecklistProgress(): number {
    if (this.checklists.length === 0) return 0;
    return Math.round((this.getCheckedCount() / this.checklists.length) * 100);
  }
}
